// src/components/lekhi/LekhiProtect.js
// SHARED by every Lekhi page (Text, Audio, MCQs, Training list, Sunday test,
// Diagnostic, Progress test review). One place for:
//   1. Watermark (faint Spocademy logo)
//   2. Copy / select / long-press block
//   3. Text rendering (bold, line breaks, powers, ॠ fix)
// Reads nothing from and writes nothing to Firestore.
import React from 'react';
import './LekhiProtect.css';

// ---------- Text normalisation (moved here from TextView, unchanged) ----------
// Superscript map for simple powers written with ^ (e.g. २^३ → २³, a^(m + n) → aᵐ⁺ⁿ)
const SUP = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
  '०': '⁰', '१': '¹', '२': '²', '३': '³', '४': '⁴', '५': '⁵', '६': '⁶', '७': '⁷', '८': '⁸', '९': '⁹',
  'a': 'ᵃ', 'b': 'ᵇ', 'k': 'ᵏ', 'm': 'ᵐ', 'n': 'ⁿ', 'x': 'ˣ', 'y': 'ʸ',
  '+': '⁺', '−': '⁻', '-': '⁻', '(': '⁽', ')': '⁾'
};

const toSup = (s) => {
  let out = '';
  for (const ch of s) {
    if (ch === ' ') continue;
    if (!SUP[ch]) return null; // anything complex (/, ×, etc.) is left untouched
    out += SUP[ch];
  }
  return out;
};

export const normalizeLekhiText = (text) => String(text ?? '')
  .replace(/\r\n/g, '\n')
  .replace(/\u0960/g, '\u090B') // ॠ → ऋ
  .replace(/\^\(([^()]*)\)/g, (m, inner) => { const s = toSup(inner); return s !== null ? s : m; })
  .replace(/\^([0-9०-९]+|[abkmnxy])/g, (m, inner) => toSup(inner) || m);

// ---------- Inline rendering: **bold** and line breaks (<br> or new line) ----------
export const renderLekhiInline = (text, keyPrefix = 'lk') => {
  const lines = String(text ?? '').split(/<br\s*\/?>|\r?\n/i);
  return lines.map((line, li) => (
    <React.Fragment key={`${keyPrefix}-l${li}`}>
      {li > 0 && <br />}
      {line.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
          return <strong key={`${keyPrefix}-${li}-${i}`} className="lk-text-bold">{part.slice(2, -2)}</strong>;
        }
        return <span key={`${keyPrefix}-${li}-${i}`}>{part.replace(/\*\*/g, '')}</span>;
      })}
    </React.Fragment>
  ));
};

// For short texts (MCQ questions, options, explanations): normalise + render
export const renderLekhiText = (text, keyPrefix = 'lk') =>
  renderLekhiInline(normalizeLekhiText(text), keyPrefix);

// ---------- Watermark ----------
export const LekhiWatermark = () => (
  <img
    src={`${process.env.PUBLIC_URL}/logo.png`}
    alt=""
    aria-hidden="true"
    className="lk-watermark"
    draggable={false}
  />
);

// ---------- Copy / cut / long-press block ----------
const blockEvent = (e) => e.preventDefault();

export const lekhiProtectProps = {
  onCopy: blockEvent,
  onCut: blockEvent,
  onContextMenu: blockEvent
};

export const LEKHI_PROTECT_CLASS = 'lk-no-select';